import React, { useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { Typography } from "common/components/Typography";
import colors from "common/styles/colors";
import { ContentCard } from "../../../components/ContentCard";
import { HazardousObject } from "../../../types/api.types";
import { AppraiserProperty } from "../../../types/appraiser.types";
import { usePropertyHazardousObjects } from "../../../hooks/hazardousObjects.hooks";
import { getHazardousObjectsSearchDescription } from "../../../helpers/hazardousObjects.helpers";
import { PropertyHazardousObjectsMap } from "./PropertyHazardousObjectsMap";
import { PropertyHazardousObjectsList } from "./PropertyHazardousObjectsList";

interface PropertyHazardousObjectsProps {
  property: AppraiserProperty;
  /** Already embedded in the shared and valuation responses, so no request is needed there. */
  hazardousObjects?: HazardousObject[];
  propertyToken?: string;
}

export const PropertyHazardousObjects: React.FC<
  PropertyHazardousObjectsProps
> = ({ property, hazardousObjects, propertyToken }) => {
  const isEmbedded = !!hazardousObjects;

  const {
    data: fetchedHazardousObjects,
    isLoading,
    isFetched,
    isError,
  } = usePropertyHazardousObjects(property._id, propertyToken, !isEmbedded);

  const objects = hazardousObjects ?? fetchedHazardousObjects;

  const indexedObjects = useMemo(
    () =>
      (objects ?? []).map((hazardousObject, index) => ({
        ...hazardousObject,
        index,
      })),
    [objects]
  );

  if (!isEmbedded && (!isFetched || isLoading)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <ContentCard
      title="Pavojingi objektai"
      caption={getHazardousObjectsSearchDescription()}
    >
      {/* An empty result means "nothing nearby", so a failed request must not look like one. */}
      {!isEmbedded && isError ? (
        <Typography variant="caption" textStyle={{ color: colors.grey }}>
          Nepavyko gauti pavojingų objektų duomenų.
        </Typography>
      ) : (
        <>
          <PropertyHazardousObjectsMap
            hazardousObjects={indexedObjects}
            property={property}
          />
          {indexedObjects.length ? (
            <PropertyHazardousObjectsList hazardousObjects={indexedObjects} />
          ) : (
            <Typography
              variant="caption"
              textStyle={{ color: colors.grey, marginTop: 20 }}
            >
              Pavojingų objektų aplink turtą nerasta.
            </Typography>
          )}
        </>
      )}
    </ContentCard>
  );
};
